import type {
  ICreditCancelEffective,
  ICreditMonthlyPlanId,
  ICreditsBalance,
  ICreditsCatalog,
  ICreditPurchase,
  ICreditPurchaseResponse,
  ICreditRefundQuote,
  ICreditRefundResponse,
  ICreditSubscription,
  ICreditSubscriptionChangeResponse,
  ICreditSubscriptionResponse,
  ICreditUsageList,
  ICreditUpgradeQuote,
} from "../models/interfaces/credits.interface.ts";
import http from "./httpService.ts";

const IDEMPOTENCY_KEY_PATTERN = /^[\x20-\x7e]{8,128}$/;

// Tells Rocky this build knows the full monthly ladder (Scale included). A request without it is treated as an
// already-open old tab: it gets the legacy catalog and cannot change a plan the old build does not know.
const LADDER_HEADERS = {"X-Glix-Plan-Ladder": "2"} as const;

export const createCreditsIdempotencyKey = (): string => crypto.randomUUID();

const idempotencyHeaders = (
  key?: string,
): typeof LADDER_HEADERS & {readonly "Idempotency-Key": string} => {
  const resolved = key ?? createCreditsIdempotencyKey();
  if (!IDEMPOTENCY_KEY_PATTERN.test(resolved)) {
    throw new RangeError("Idempotency-Key must be 8-128 printable ASCII characters");
  }
  return {...LADDER_HEADERS, "Idempotency-Key": resolved};
};

const workspaceCreditsPath = (workspaceId: string): string =>
  `/api/v1/workspace/${workspaceId}/credits`;

export const getCreditsBalance = async (workspaceId: string): Promise<ICreditsBalance> => {
  const response = await http.get<ICreditsBalance>(workspaceCreditsPath(workspaceId), {headers: LADDER_HEADERS});
  return response.data;
};

export const getCreditsCatalog = async (): Promise<ICreditsCatalog> => {
  const response = await http.get<ICreditsCatalog>("/api/v1/credits/catalog", {headers: LADDER_HEADERS});
  return response.data;
};

export const purchaseCreditPackage = async (
  workspaceId: string,
  packageId: string,
  idempotencyKey?: string,
): Promise<ICreditPurchaseResponse> => {
  const response = await http.post<ICreditPurchaseResponse>(
    `${workspaceCreditsPath(workspaceId)}/purchases`,
    {packageId},
    {headers: idempotencyHeaders(idempotencyKey)},
  );
  return response.data;
};

export const getCreditPurchases = async (workspaceId: string): Promise<ICreditPurchase[]> => {
  const response = await http.get<{purchases: ICreditPurchase[]}>(
    `${workspaceCreditsPath(workspaceId)}/purchases`,
    {headers: LADDER_HEADERS},
  );
  return response.data.purchases;
};

export const getCreditUsage = async (workspaceId: string, limit = 50): Promise<ICreditUsageList> => {
  const response = await http.get<ICreditUsageList>(
    `${workspaceCreditsPath(workspaceId)}/usage`,
    {headers: LADDER_HEADERS, params: {limit}},
  );
  return response.data;
};

export const getCreditPurchase = async (
  workspaceId: string,
  purchaseId: string,
  signal?: AbortSignal,
): Promise<ICreditPurchase> => {
  const response = await http.get<ICreditPurchase>(
    `${workspaceCreditsPath(workspaceId)}/purchases/${purchaseId}`,
    {signal, headers: LADDER_HEADERS},
  );
  return response.data;
};

export const getCreditSubscription = async (
  workspaceId: string,
): Promise<ICreditSubscription | null> => {
  const response = await http.get<ICreditSubscriptionResponse>(
    `${workspaceCreditsPath(workspaceId)}/subscription`,
    {headers: LADDER_HEADERS},
  );
  return response.data.subscription;
};

export const getCreditUpgradeQuote = async (
  workspaceId: string,
  planId: ICreditMonthlyPlanId,
): Promise<ICreditUpgradeQuote> => {
  const response = await http.post<ICreditUpgradeQuote>(
    `${workspaceCreditsPath(workspaceId)}/subscription/upgrade-quote`,
    {planId},
    {headers: LADDER_HEADERS},
  );
  return response.data;
};

export const upgradeCreditSubscription = async (
  workspaceId: string,
  planId: ICreditMonthlyPlanId,
  expectedVersion: number,
  idempotencyKey?: string,
): Promise<ICreditSubscriptionChangeResponse> => {
  const response = await http.post<ICreditSubscriptionChangeResponse>(
    `${workspaceCreditsPath(workspaceId)}/subscription/upgrade`,
    {planId, expectedVersion},
    {headers: idempotencyHeaders(idempotencyKey)},
  );
  return {...response.data, httpStatus: response.status};
};

export const changeNextCreditPlan = async (
  workspaceId: string,
  planId: ICreditMonthlyPlanId,
  expectedVersion: number,
  idempotencyKey?: string,
): Promise<ICreditSubscriptionChangeResponse> => {
  const response = await http.post<ICreditSubscriptionChangeResponse>(
    `${workspaceCreditsPath(workspaceId)}/subscription/change-next-plan`,
    {planId, expectedVersion},
    {headers: idempotencyHeaders(idempotencyKey)},
  );
  return {...response.data, httpStatus: response.status};
};

export const cancelCreditSubscription = async (
  workspaceId: string,
  effective: ICreditCancelEffective,
  expectedVersion: number,
  idempotencyKey?: string,
): Promise<ICreditSubscriptionChangeResponse> => {
  const response = await http.post<ICreditSubscriptionChangeResponse>(
    `${workspaceCreditsPath(workspaceId)}/subscription/cancel`,
    {effective, expectedVersion},
    {headers: idempotencyHeaders(idempotencyKey)},
  );
  return {...response.data, httpStatus: response.status};
};

export const resumeCreditSubscription = async (
  workspaceId: string,
  expectedVersion: number,
  idempotencyKey?: string,
): Promise<ICreditSubscriptionChangeResponse> => {
  const response = await http.post<ICreditSubscriptionChangeResponse>(
    `${workspaceCreditsPath(workspaceId)}/subscription/resume`,
    {expectedVersion},
    {headers: idempotencyHeaders(idempotencyKey)},
  );
  return {...response.data, httpStatus: response.status};
};

export const getCreditRefundQuote = async (
  workspaceId: string,
  purchaseId: string,
  credits: number,
): Promise<ICreditRefundQuote> => {
  const response = await http.post<ICreditRefundQuote>(
    `${workspaceCreditsPath(workspaceId)}/purchases/${purchaseId}/refund-quote`,
    {credits},
    {headers: LADDER_HEADERS},
  );
  return response.data;
};

export const requestCreditRefund = async (
  workspaceId: string,
  purchaseId: string,
  credits: number,
  expectedVersion: number,
  idempotencyKey?: string,
): Promise<ICreditRefundResponse> => {
  const response = await http.post<ICreditRefundResponse>(
    `${workspaceCreditsPath(workspaceId)}/purchases/${purchaseId}/refunds`,
    {credits, expectedVersion},
    {headers: idempotencyHeaders(idempotencyKey)},
  );
  return {...response.data, httpStatus: response.status};
};

export const getCreditRefund = async (
  workspaceId: string,
  refundId: string,
): Promise<ICreditRefundResponse> => {
  const response = await http.get<ICreditRefundResponse>(
    `${workspaceCreditsPath(workspaceId)}/refunds/${refundId}`,
    {headers: LADDER_HEADERS},
  );
  return {...response.data, httpStatus: response.status};
};

export const getBalance = getCreditsBalance;
export const purchase = purchaseCreditPackage;
export const listPurchases = getCreditPurchases;
export const listUsage = getCreditUsage;
export const getPurchase = getCreditPurchase;
export const getSubscription = getCreditSubscription;
export const upgradeQuote = getCreditUpgradeQuote;
export const upgrade = upgradeCreditSubscription;
export const changeNextPlan = changeNextCreditPlan;
export const cancel = cancelCreditSubscription;
export const resume = resumeCreditSubscription;
export const refundQuote = getCreditRefundQuote;
export const requestRefund = requestCreditRefund;
export const getRefund = getCreditRefund;
